import React, { useEffect, useState } from "react"
import { motion } from "framer-motion"
import { useTranslation } from "react-i18next"
import theme from "lib/material-ui/theme"
import { styled, Theme } from "@mui/material/styles"
import Stack from "@mui/material/Stack"
import { useLazyLikeWishItemQuery } from "appRedux/slices/wishItemApiSlice"
import { isPublicPage } from "appRedux/baseQuery"
import { ITEM_COMMENT_BUTTON_CLICKED, ITEM_LIKE_COUNT_CLICKED } from "constants/events"
import LikeIcon from "components/atoms/_icons/LikeIcon"
import CommentIcon from "components/atoms/_icons/CommentIcon"
import IconWrapper from "components/atoms/IconWrapper"
import LoginRequiredDialog from "components/molecules/LoginRequiredDialog"

type WishItemReactionBoxProps = {
  id: number;
  isLiked: boolean;
  likeCount: number;
  commentCount: number;
  color?: string;
  onLikeButtonClick?: () => void;
  onLikeCountClick?: () => void;
  onCommentButtonClick?: () => void;
}

export default function WishItemReactionBox(props: WishItemReactionBoxProps) {
  const {
    id,
    isLiked,
    likeCount,
    commentCount,
    color = theme?.palette.gray["14"],
    onLikeButtonClick = new Function(),
    onLikeCountClick = new Function(),
    onCommentButtonClick = new Function()
  } = props

  const [localIsLiked, setLocalIsLiked] = useState(false)
  const [loginRequiredDialogReason, setLoginRequiredDialogReason] = useState<"like" | "comment" | null>(null)

  const [triggerLikeWishItem] = useLazyLikeWishItemQuery()

  const { t } = useTranslation()

  useEffect(() => {
    setLocalIsLiked(isLiked)
  }, [isLiked])

  function handleLikeButtonClicked() {
    if (isPublicPage()) {
      setLoginRequiredDialogReason("like")
      return
    }
    setLocalIsLiked(!localIsLiked)
    triggerLikeWishItem(id)
    onLikeButtonClick()
  }

  function handleCloseLoginRequiredDialog() {
    setLoginRequiredDialogReason(null)
  }

  function getLoginRequiredDialogMessage() {
    return loginRequiredDialogReason === "like"
      ? t("auth.loginRequiredToLike")
      : t("auth.loginRequiredToComment")
  }

  function handleLikeCountClicked() {
    if (likeCount === 0) return
    document.dispatchEvent(new CustomEvent(ITEM_LIKE_COUNT_CLICKED))
    onLikeCountClick()
  }

  function handleSaveButtonClicked() {
  }

  function handleCommentButtonClicked() {
    if (isPublicPage()) {
      setLoginRequiredDialogReason("comment")
      return
    }
    document.dispatchEvent(new CustomEvent(ITEM_COMMENT_BUTTON_CLICKED))
    onCommentButtonClick()
  }

  function handleShareButtonClicked() {

  }

  function renderMotionButton(icon: React.ReactNode, onClick: () => void) {
    return (
      <motion.div
        style={{ width: "100%", height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}
        whileTap={{ scale: 1.1 }}
        onClick={onClick}
      >
        {icon}
      </motion.div>
    )
  }

  return (
    <Container>
      <LikeButton className="like-button" filled={localIsLiked ? 1 : 0} color={color}>
        {renderMotionButton(<LikeIcon/>, handleLikeButtonClicked)}
        <Count color={color} onClick={handleLikeCountClicked}>{likeCount}</Count>
      </LikeButton>

      <ReactionButton className="comment-button" filled={0} color={color}>
        {renderMotionButton(<CommentIcon/>, handleCommentButtonClicked)}
        <Count color={color} onClick={handleCommentButtonClicked}>{commentCount}</Count>
      </ReactionButton>

      {/*<AddBookmarkButton color={color} filled={0}>*/}
      {/*  {renderMotionButton(<AddBookmarkIcon/>, handleSaveButtonClicked)}*/}
      {/*</AddBookmarkButton>*/}

      {/*<ShareButton color={color} filled={0}>*/}
      {/*  {renderMotionButton(<SendIcon/>, handleShareButtonClicked)}*/}
      {/*</ShareButton>*/}
      <LoginRequiredDialog
        open={loginRequiredDialogReason !== null}
        message={getLoginRequiredDialogMessage()}
        onClose={handleCloseLoginRequiredDialog}
      />
    </Container>
  )
}

const Container = styled(Stack)(({ theme }) => ({
  flexDirection: "row-reverse",
  alignItems: "baseline",
  marginTop: "auto"
}))

type ReactButtonProps = {
  theme?: Theme;
  filled: 1 | 0;
  color?: string;
}

const LikeButton = styled(IconWrapper)(({ theme, filled, color }: ReactButtonProps) => ({
  "& svg": {
    fill: filled ? theme?.palette.error["4"] : "transparent",
    stroke: filled ? theme?.palette.error["4"] : color,
    transition: "0.3s"
  }
}))

const AddBookmarkButton = styled(IconWrapper)(({ theme, filled, color }: ReactButtonProps) => ({
  "& svg": {
    fill: filled ? color : "transparent",
    stroke: filled ? color : color,
    strokeWidth: 1.5,
    transition: "0.3s"
  }
}))

const ShareButton = styled(IconWrapper)(({ theme, filled, color }: ReactButtonProps) => ({
  "& svg": {
    stroke: color,
    strokeWidth: 1.5,
    transition: "0.3s"
  }
}))

const ReactionButton = styled(IconWrapper)(({ theme, filled, color }: ReactButtonProps) => ({
  "& svg": {
    strokeWidth: 1.5,
    transition: "0.3s",
    fill: color
  }
}))

const Count = styled("span")(({ theme, color }) => ({
  color: color,
  fontSize: "1.1rem",
  fontFamily: "VazirmatnFD, serif !important",
  minWidth: "44px",
  textAlign: "center",
  lineHeight: 1
}))
